import React, { useEffect, useState } from 'react';
import { SectionHeader } from '../components/UI';
import { api, moneyFull } from '../lib/api';
import { AlertTriangle, TrendingUp, TrendingDown, ChevronDown, ChevronRight, FileText, ExternalLink } from 'lucide-react';

// Budget vs Actuals (Feb 2026 M9 Phase 1)
// Rendered from the AUTHORITATIVE "Corrected BvA" Google Sheet — an AI-optimised,
// human-curated view of QuickBooks output with Ad Spend re-allocated, dormant
// $0 lines removed, unbudgeted accounts flagged, and a notes column carrying
// variance narrative. Raw QBO ingest continues silently for reconciliation
// (drift surfaces on Integrity).

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];

function ExpandChevron({ open }) {
  return open ? <ChevronDown size={14} strokeWidth={1.5} /> : <ChevronRight size={14} strokeWidth={1.5} />;
}

function VarianceBadge({ pctOfBudget, unbudgeted }) {
  if (unbudgeted) {
    return (
      <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 bg-amber-500 text-indie-ink font-semibold border border-amber-600">
        no budget
      </span>
    );
  }
  if (pctOfBudget == null) return <span className="text-indie-ink/30">—</span>;
  const pct = pctOfBudget * 100;
  const cls = pct <= 90 ? 'bg-emerald-50 text-emerald-700 border-emerald-600'
    : pct <= 110 ? 'bg-indie-cream text-indie-ink border-indie-ink/30'
    : pct <= 130 ? 'bg-amber-50 text-amber-800 border-amber-500'
    : 'bg-rose-50 text-rose-700 border-rose-500';
  return (
    <span className={`text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 border ${cls} font-semibold num-tabular`}>
      {Math.round(pct)}%
    </span>
  );
}

function LineItemRow({ item }) {
  const under = (item.variance_dollars ?? 0) < 0;
  return (
    <tr className="border-b border-indie-ink/5" data-testid={`bva-item-${item.account.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <td className="py-1.5 pr-3 text-indie-ink">
        <div className="flex items-center gap-2">
          <span>{item.account}</span>
          {item.notes && (
            <span title={item.notes} className="text-indie-ink/50">
              <FileText size={11} strokeWidth={1.5} />
            </span>
          )}
        </div>
        {item.notes && (
          <div className="text-[10px] text-indie-ink/50 leading-tight mt-0.5">{item.notes}</div>
        )}
      </td>
      {MONTHS.map((m) => {
        const cell = item.monthly.find((x) => x.month === m);
        return <td key={m} className="py-1.5 px-1 text-right num-tabular text-xs text-indie-ink/70">
          {cell?.value == null ? <span className="text-indie-ink/25">—</span> : moneyFull(cell.value)}
        </td>;
      })}
      <td className="py-1.5 px-2 text-right num-tabular font-semibold text-indie-ink">{moneyFull(item.ytd_actual)}</td>
      <td className="py-1.5 px-2 text-right num-tabular text-indie-ink/70">{moneyFull(item.ytd_budget)}</td>
      <td className={`py-1.5 px-2 text-right num-tabular font-semibold ${under ? 'text-emerald-700' : 'text-rose-700'}`}>
        {item.variance_dollars == null ? '—' : (under ? '' : '+') + moneyFull(item.variance_dollars)}
      </td>
      <td className="py-1.5 px-2 text-center">
        <VarianceBadge pctOfBudget={item.pct_of_budget} unbudgeted={item.unbudgeted} />
      </td>
      <td className="py-1.5 pl-2 text-right num-tabular text-indie-ink/70">{moneyFull(item.fy26_budget)}</td>
      <td className="py-1.5 px-2 text-right num-tabular text-indie-ink/70">{moneyFull(item.run_rate_fy26)}</td>
    </tr>
  );
}

function Section({ section, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const variancePctOfBudget = section.pct_of_budget;
  return (
    <div className="border border-indie-ink/15 bg-white" data-testid={`bva-section-${section.label.toLowerCase().replace(/\s+/g, '-')}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-indie-cream/50"
      >
        <div className="flex items-center gap-2">
          <ExpandChevron open={open} />
          <div className="text-sm font-semibold text-indie-ink uppercase tracking-[0.14em]">{section.label}</div>
          <span className="text-[10px] text-indie-ink/50">{section.item_count} line{section.item_count === 1 ? '' : 's'}</span>
          {section.unbudgeted_count > 0 && (
            <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 bg-amber-500 text-indie-ink font-semibold" title={`${section.unbudgeted_count} unbudgeted accounts`}>
              {section.unbudgeted_count} unbudgeted
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="text-right">
            <div className="text-indie-ink font-semibold num-tabular">{moneyFull(section.ytd_actual)}</div>
            <div className="text-[10px] text-indie-ink/50">YTD actual</div>
          </div>
          <div className="text-right">
            <div className="text-indie-ink/70 num-tabular">{moneyFull(section.ytd_budget)}</div>
            <div className="text-[10px] text-indie-ink/50">YTD budget</div>
          </div>
          <VarianceBadge pctOfBudget={variancePctOfBudget} unbudgeted={false} />
        </div>
      </button>
      {open && (
        <div className="border-t border-indie-ink/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50 font-semibold bg-indie-cream/50">
                <th className="text-left py-2 pr-3 pl-5">Account</th>
                {MONTHS.map((m) => <th key={m} className="text-right py-2 px-1">{m}</th>)}
                <th className="text-right py-2 px-2">YTD Actual</th>
                <th className="text-right py-2 px-2">YTD Budget</th>
                <th className="text-right py-2 px-2">Variance $</th>
                <th className="text-center py-2 px-2">% Budget</th>
                <th className="text-right py-2 pl-2">FY26 Budget</th>
                <th className="text-right py-2 px-2 pr-5">Run-rate FY26</th>
              </tr>
            </thead>
            <tbody>
              {section.items.map((item) => (
                <LineItemRow key={`${item.section}::${item.account}`} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, actual, budget, testId }) {
  const variance = (actual ?? 0) - (budget ?? 0);
  const pct = budget ? actual / budget : null;
  const positive = variance >= 0;
  const isExpense = label.toLowerCase().includes('expense');
  const good = isExpense ? !positive : positive;
  const Icon = good ? TrendingUp : TrendingDown;
  return (
    <div className={`border-2 p-5 ${good ? 'border-emerald-600 bg-emerald-50' : 'border-rose-500 bg-rose-50'}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-semibold text-indie-ink/60">
        {label}
        <Icon size={12} className={good ? 'text-emerald-700' : 'text-rose-600'} strokeWidth={1.5} />
      </div>
      <div className="mt-2 text-2xl font-semibold text-indie-ink num-tabular">{moneyFull(actual)}</div>
      <div className="mt-1 text-xs text-indie-ink/70">
        Budget {moneyFull(budget)}
        {pct != null && <> · <span className="font-semibold">{Math.round(pct * 100)}%</span></>}
      </div>
      <div className={`mt-1 text-xs font-semibold num-tabular ${good ? 'text-emerald-700' : 'text-rose-700'}`}>
        {positive ? '+' : ''}{moneyFull(variance)} vs budget
      </div>
    </div>
  );
}

export default function BudgetVsActuals() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/bva/summary')
      .then((r) => setD(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="label-editorial" data-testid="bva-loading">Loading Budget vs Actuals…</div>;
  if (!d || d.ok === false) {
    return (
      <div data-testid="bva-empty" className="border border-amber-500 bg-amber-50 p-5">
        <div className="font-semibold text-indie-ink">Corrected BvA sheet not synced yet.</div>
        <div className="mt-1 text-sm text-indie-ink/70">{d?.reason || 'Trigger a sync from Integrations, or wait for the next scheduled refresh.'}</div>
      </div>
    );
  }

  const kpis = d.kpis || {};
  const lastSynced = d.last_synced ? new Date(d.last_synced).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div className="space-y-6" data-testid="bva-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <SectionHeader
          eyebrow="Report"
          title="Budget vs Actuals · FY26"
          description={`AI-optimised view from QuickBooks — Ad Spend re-allocated, dormant $0 lines removed, unbudgeted accounts flagged. Actuals through ${(d.months_populated && d.months_populated.length ? d.months_populated[d.months_populated.length - 1] : 'Jul')} 2026.`}
        />
        <div className="text-right text-[11px] text-indie-ink/60">
          <div>Synced {lastSynced}</div>
          {d.source_url && (
            <a href={d.source_url} target="_blank" rel="noopener noreferrer"
               className="mt-0.5 inline-flex items-center gap-1 text-indie-cobalt hover:underline uppercase tracking-[0.12em]" data-testid="bva-source-link">
              open sheet <ExternalLink size={10} strokeWidth={1.5} />
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="bva-kpis">
        <KpiCard label="Total Income · YTD"   actual={kpis.total_income}   budget={kpis.total_income_budget}   testId="bva-kpi-income" />
        <KpiCard label="Total Expenses · YTD" actual={kpis.total_expenses} budget={kpis.total_expenses_budget} testId="bva-kpi-expenses" />
        <KpiCard label="Net Income · YTD"     actual={kpis.net_income}     budget={kpis.net_income_budget}     testId="bva-kpi-net" />
      </div>

      {/* Issues list — variance-first triage surface */}
      {d.issues && d.issues.length > 0 && (
        <div className="border border-amber-500 bg-amber-50/50 p-5" data-testid="bva-issues">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-700" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
              Things worth looking at · {d.issues.length}
            </div>
          </div>
          <ul className="mt-3 divide-y divide-amber-500/20">
            {d.issues.slice(0, 15).map((iss, i) => (
              <li key={i} className="py-2 flex items-start justify-between gap-3" data-testid={`bva-issue-${i}`}>
                <div className="min-w-0">
                  <div className="text-xs">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50 mr-1">{iss.section}</span>
                    <span className="font-semibold text-indie-ink">{iss.account}</span>
                  </div>
                  <div className="text-[11px] text-indie-ink/70 leading-tight mt-0.5">{iss.reason}</div>
                </div>
                <div className="text-right text-xs whitespace-nowrap num-tabular">
                  <span className="font-semibold text-indie-ink">{moneyFull(iss.ytd_actual)}</span>
                  <span className="text-indie-ink/50"> vs {moneyFull(iss.ytd_budget || 0)}</span>
                </div>
              </li>
            ))}
          </ul>
          {d.issues.length > 15 && (
            <div className="mt-2 text-[10px] text-indie-ink/50">Showing top 15 of {d.issues.length} issues.</div>
          )}
        </div>
      )}

      {/* Sections — collapsible, income + 5 expense buckets + other income */}
      <div className="space-y-2">
        {d.sections.map((section) => (
          <Section key={section.label} section={section} defaultOpen={section.label === 'Income'} />
        ))}
      </div>

      <div className="text-[10px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
        Source: Corrected BvA sheet · Ad Spend allocated from parent · dormant $0 lines removed · unbudgeted accounts flagged. Raw QuickBooks ingest continues in the background for reconciliation; drift will surface on the Integrity page.
      </div>
    </div>
  );
}
